import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  UserCredential,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { auth } from '../firebase';
import { config } from '../config';

interface AuthContextType {
  currentUser: User | null;
  jwtToken: string | null;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  loginWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function getJwtToken(firebaseUser: User) {
    try {
      const idToken = await firebaseUser.getIdToken();
              const response = await fetch(`${config.API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ firebase_token: idToken }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setJwtToken(data.access_token);
        localStorage.setItem('jwt_token', data.access_token);
      } else {
        console.error('Failed to get JWT token');
      }
    } catch (error) {
      console.error('Error getting JWT token:', error);
    }
  }

  function signup(email: string, password: string) {
    localStorage.setItem('hasLoggedInBefore', 'true');
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email: string, password: string) {
    localStorage.setItem('hasLoggedInBefore', 'true');
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function loginWithGoogle() {
    localStorage.setItem('hasLoggedInBefore', 'true');
    const provider = new GoogleAuthProvider();
    // Add scopes if needed
    provider.addScope('email');
    provider.addScope('profile');
    
    try {
      // Try popup first, fallback to redirect
      return await signInWithPopup(auth, provider);
    } catch (error: any) {
      // If popup is blocked, use redirect
      if (error.code === 'auth/popup-blocked') {
        return signInWithRedirect(auth, provider);
      }
      throw error;
    }
  }

  async function logout() {
    setJwtToken(null);
    localStorage.removeItem('jwt_token');
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await getJwtToken(user);
      } else {
        setJwtToken(null);
        localStorage.removeItem('jwt_token');
      }
      setLoading(false);
    });

    // Handle redirect result for Google auth
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // User signed in via redirect
          await getJwtToken(result.user);
        }
      } catch (error) {
        console.error('Error handling redirect result:', error);
      }
    };

    handleRedirectResult();

    // Check for existing JWT token on app load
    const existingToken = localStorage.getItem('jwt_token');
    if (existingToken) {
      setJwtToken(existingToken);
    }

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    jwtToken,
    login,
    signup,
    loginWithGoogle,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 